import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { LoginPage } from '@/pages/auth/LoginPage'
import { RegisterPage } from '@/pages/auth/RegisterPage'
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage'
import { DashboardPage } from '@/pages/dashboard/DashboardPage'
import { POSPage } from '@/pages/pos/POSPage'
import { InventoryPage } from '@/pages/inventory/InventoryPage'
import { CustomersPage } from '@/pages/customers/CustomersPage'
import { EmployeesPage } from '@/pages/employees/EmployeesPage'
import { ReportsPage } from '@/pages/reports/ReportsPage'
import { SettingsPage } from '@/pages/settings/SettingsPage'
import { ProfilePage } from '@/pages/profile/ProfilePage'
import { HelpPage } from '@/pages/help/HelpPage'
import { ProcurementPage } from '@/pages/procurement/ProcurementPage'
import { TransactionHistoryPage } from '@/pages/transactions/TransactionHistoryPage'
import { ExpensesPage } from '@/pages/expenses/ExpensesPage'
import { DocumentsPage } from '@/pages/documents/DocumentsPage'
import { BillPaymentsPage } from '@/pages/bill-payments/BillPaymentsPage'
import { DashboardLayout } from '@/components/layouts/DashboardLayout'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { ThemeProvider } from '@/contexts/ThemeContext'
import { NotificationProvider } from '@/contexts/NotificationContext'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { ErrorBoundary } from '@/components/ui/ErrorBoundary'


/**
 * Role-based route access:
 * - Dashboard: All authenticated users
 * - POS: All authenticated users (cashier, staff, manager, admin)
 * - Inventory: Manager and Admin only (staff can view)
 * - Customers: Cashier and above
 * - Employees: Admin only
 * - Reports: Manager and Admin only
 * - AI Insights: Manager and Admin only
 * - Settings: Admin only
 * - Profile: All authenticated users
 */

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth()

  // Show loading state while checking authentication
  if (loading) {
    return (
      <LoadingSpinner
        variant="fullscreen"
        size="lg"
        message="Initializing"
        submessage="Please wait..."
      />
    )
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Navigate to="/auth/login" replace />
          )
        }
      />
      <Route
        path="/auth/login"
        element={
          isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <LoginPage />
          )
        }
      />
      <Route
        path="/auth/register"
        element={
          isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <RegisterPage />
          )
        }
      />
      <Route
        path="/auth/forgot-password"
        element={
          isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <ForgotPasswordPage />
          )
        }
      />

      {/* Protected Routes - All authenticated users */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <DashboardPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/pos"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <POSPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ProfilePage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <HelpPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Protected Routes - Cashier and above */}
      <Route
        path="/customers"
        element={
          <ProtectedRoute minRole="cashier">
            <DashboardLayout>
              <CustomersPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/bill-payments"
        element={
          <ProtectedRoute minRole="cashier">
            <DashboardLayout>
              <BillPaymentsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Protected Routes - Manager and above */}
      <Route
        path="/inventory"
        element={
          <ProtectedRoute minRole="manager">
            <DashboardLayout>
              <InventoryPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports"
        element={
          <ProtectedRoute minRole="manager">
            <DashboardLayout>
              <ReportsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/ai-insights"
        element={<Navigate to="/reports?tab=ai-insights" replace />}
      />

      {/* Procurement: Suppliers + GRN (Manager and above) */}
      <Route
        path="/procurement"
        element={
          <ProtectedRoute minRole="manager">
            <DashboardLayout>
              <ProcurementPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/suppliers" element={<Navigate to="/procurement?tab=suppliers" replace />} />
      <Route path="/grn" element={<Navigate to="/procurement?tab=grn" replace />} />

      {/* Transaction History - Cashier and above */}
      <Route
        path="/transactions"
        element={
          <ProtectedRoute minRole="cashier">
            <DashboardLayout>
              <TransactionHistoryPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/expenses"
        element={
          <ProtectedRoute minRole="manager">
            <DashboardLayout>
              <ExpensesPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Protected Routes - Admin only */}
      <Route
        path="/documents"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <DocumentsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/cheques" element={<Navigate to="/documents?tab=cheques" replace />} />

      <Route
        path="/employees"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <EmployeesPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute requiredRoles={['admin']}>
            <DashboardLayout>
              <SettingsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <NotificationProvider>
          <AuthProvider>
            <Router>
              <div className="min-h-screen theme-transition">
                <AppRoutes />
              </div>
            </Router>
          </AuthProvider>
        </NotificationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}

export default App